<template>
    <div>
        <!-- E-MAIL -->
        <div class="form-group">
            <ValidationProvider
                name="webUserEmail"
                :rules="{
                    required: true,
                    email: true
                }"
                v-slot="{ errors }"
            >
                <label for="webUserEmailInput">{{ $t('email') }}</label>
                <input type="text" id="webUserEmailInput" class="form-control" v-model="webUser.email" />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- FIRST NAME -->
        <div class="form-group">
            <ValidationProvider
                name="webUserFirstName"
                :rules="{
                    required: true
                }"
                v-slot="{ errors }"
            >
                <label for="webUserFirstNameInput">{{ $t('first_name') }}</label>
                <input type="text" id="webUserFirstNameInput" class="form-control" v-model="webUser.first_name" />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- LAST NAME -->
        <div class="form-group">
            <ValidationProvider
                name="webUserLastName"
                :rules="{
                    required: true
                }"
                v-slot="{ errors }"
            >
                <label for="webUserLastNameInput">{{ $t('last_name') }}</label>
                <input type="text" id="webUserLastNameInput" class="form-control" v-model="webUser.last_name" />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- PHARMACY (SHOWN IF CURRENT USER IS SUPERADMIN AND TARGET WEB USER'S ROLE ISN'T SUPERADMIN) -->
        <div class="form-group" v-if="user.permissions.superadmin && webUser.role !== 'superadmin'">
            <ValidationProvider name="webUserPharmacy" rules="required" v-slot="{ errors }">
                <label for="webUserPharmacySelection">{{ $t('pharmacy') }}</label>
                <!-- disable field if editing (pharmacy cannot be changed after the fact) -->
                <input
                    v-if="edit"
                    class="form-control"
                    disabled
                    id="webUserPharmacySelection"
                    :value="getPharmacyName(webUser.pharmacy_id)"
                />
                <!-- otherwise show pharmacy selection -->
                <select v-else id="webUserPharmacySelection" class="form-control" v-model="webUser.pharmacy_id">
                    <option class="form-control" disabled>
                        {{ `${$t('select')} ${$t('pharmacy').toLowerCase()}` }}
                    </option>
                    <option v-for="pharmacy in pharmacies" v-bind:key="pharmacy.id" v-bind:value="pharmacy.id">
                        {{ pharmacy.information.name }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- ROLE :show if it is not MyAccount page (target user id !== session user id) -->
        <div class="form-group" v-if="webUser.id !== user.id">
            <ValidationProvider name="webUserRole" :rules="{ required: true }" v-slot="{ errors }">
                <label for="webUserRoleSelection">{{ $t('role') }}</label>
                <!-- disable field if editing and either target user is superadmin or current user doesn't have editing permissions -->
                <input
                    v-if="edit && (webUser.role === 'superadmin' || !user.permissions.edit_user)"
                    class="form-control"
                    disabled
                    id="webUserRoleSelection"
                    :value="$t(`role_${webUser.role}`)"
                />
                <!-- otherwise show role selection -->
                <select v-else class="form-control" id="webUserRoleSelection" v-model="webUser.role">
                    <option disabled>{{ $t('select') }} {{ $t('role').toLowerCase() }}</option>
                    <option v-for="role in roles" v-bind:key="role" v-bind:value="role">
                        {{ role ? $t(`role_${role}`) : '' }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- LANGUAGE -->
        <div class="form-group">
            <ValidationProvider name="webUserLanguage" :rules="{ required: true }" v-slot="{ errors }">
                <label for="webUserLanguageSelection">{{ $t('language') }}</label>
                <select id="webUserLanguageSelection" class="form-control" v-model="webUser.language">
                    <option disabled>{{ $t('select') }} {{ $t('language').toLowerCase() }}</option>
                    <option v-for="lang in locales" v-bind:key="lang" v-bind:value="lang">
                        {{ $t(`lang_${lang}`) }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- STATUS :show if it is not MyAccount page (target user id !== session user id) -->
        <div class="form-group" v-if="webUser.id !== user.id">
            <ValidationProvider name="webUserStatus" :rules="{ required: true }" v-slot="{ errors }">
                <label for="webUserStatusSelection">{{ $t('status') }}</label>
                <select id="webUserStatusSelection" class="form-control" v-model="webUser.status">
                    <option disabled>{{ $t('select') }} {{ $t('status').toLowerCase() }}</option>
                    <option v-for="status in statuses" v-bind:key="status" v-bind:value="status">
                        {{ $t(status) }}
                    </option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- Enabling account from incorrect logins -->
        <div class="form-group" v-if="user.permissions.superadmin && webUser.incorrect_logins >= 3">
            <ValidationProvider name="webUserIncorrectLoginsEnable" v-slot="{ errors }">
                <label for="webUserEnableAccount">{{ $t('enable_account') }}</label>
                <select id="webUserEnableAccount" class="form-control" v-model="webUser.enable_account">
                    <option disabled>{{ $t('activate_account')}}</option>
                    <option value="activate">{{ $t('activate') }}</option>
                </select>
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
        <!-- SESSION LENGTH -->
        <div class="form-group">
            <ValidationProvider
                name="webUserSessionLength"
                :rules="{
                    integer: true,
                    maxValue: { maxValue: sessionLengthMax },
                    minValue: { minValue: sessionLengthMin }
                }"
                v-slot="{ errors }"
                vid="webUserSessionLength"
            >
                <label for="webUserSessionLengthInput" v-b-tooltip.hover :title="$t('session_length_tooltip')">
                    {{ $t('session_length') }}
                </label>
                <div class="input-group">
                    <input
                        type="number"
                        :min="sessionLengthMin"
                        :max="sessionLengthMax"
                        class="form-control"
                        v-model="webUser.session_length"
                        id="webUserSessionLengthInput"
                    />
                    <div class="input-group-append">
                        <span class="input-group-text">{{ $t('hours_partitive') }}</span>
                    </div>
                </div>
                <div class="invalid-feedback" style="display: block">
                    <span>
                        {{ $t(errors[0]) }}
                        {{ errors[0] === 'error_value_too_high' ? sessionLengthMax : '' }}
                        {{ errors[0] === 'error_value_too_low' ? sessionLengthMin : '' }}
                    </span>
                </div>
            </ValidationProvider>
        </div>
        <div class="form-group">
            <ValidationProvider
                name="webUserPassword"
                vid="webUserPassword"
                :rules="{
                    required: !edit,
                    min: passwordMinLength
                }"
                v-slot="{ errors }"
            >
                <label for="webUserPasswordInput">{{ $t('password') }}</label>
                <button v-if="!edit" class="btn btn-sm btn-primary ml-sm-4" v-on:click.prevent="generatePassword">
                    {{ $t('generate') }}
                </button>
                <input type="text" autocomplete="off" class="form-control" v-model="webUser.password" id="webUserPasswordInput" />
                <div class="invalid-feedback" style="display: block">
                    <span v-show="passwordMinLength"
                        >{{ $t(errors[0]) }}
                        {{ errors[0] === 'error_too_short' ? `${passwordMinLength}` : null }}
                    </span>
                </div>
            </ValidationProvider>
        </div>
        <!-- PASSWORD CONFIRM -->
        <div class="form-group">
            <ValidationProvider
                name="webUserPasswordConfirmation"
                :rules="{
                    required: webUser.password || !edit,
                    confirmed: 'webUserPassword'
                }"
                v-slot="{ errors }"
                vid="webUserPasswordConfirmation"
            >
                <label for="webUserPasswordConfirmationInput">{{ $t('confirm_password') }}</label>
                <input
                    type="text"
                    autocomplete="off"
                    class="form-control"
                    v-model="webUser.password_confirmation"
                    id="webUserPasswordConfirmationInput"
                />
                <div class="invalid-feedback" style="display: block">
                    <span>{{ $t(errors[0]) }}</span>
                </div>
            </ValidationProvider>
        </div>
    </div>
</template>

<script>
import randomstring from 'randomstring';
import { mapState } from 'vuex';
import { getRoles, handleAxiosError } from '@/services/axiosService';
import { locales } from '@/services/translationService';
export default {
    props: {
        edit: {
            required: true,
            type: Boolean
        },
        pharmacies: {
            required: false,
            type: Array,
            default: () => []
        },
        webUser: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            error: false,
            locales,
            passwordMinLength: 6,
            roles: [],
            sessionLengthMax: 8,
            sessionLengthMin: 1,
            statuses: ['active', 'inactive'],
            webUserOriginalPharmacyId: null
        };
    },
    async created() {
        if (this.user.permissions.superadmin) {
            // current user is superadmin: store target user's initial pharmacy_id
            this.webUserOriginalPharmacyId = _.clone(this.webUser.pharmacy_id);
        }
        // get roles
        await this.getRoles();
        if (!this.error) {
            // set form loaded
            this.$emit('loaded', 'form');
        }
    },
    computed: {
        ...mapState('account', ['user']),
        role() {
            return this.webUser.role;
        }
    },
    methods: {
        // generate random eight-digit password
        generatePassword() {
            const pass = randomstring.generate(8);
            this.webUser.password_confirmation = this.webUser.password = pass;
        },
        // get pharmacy name by id
        getPharmacyName(id) {
            const pharmacy = this.pharmacies.filter(pharmacy => pharmacy.id === id)[0];
            if (!pharmacy) {
                return null;
            }
            return pharmacy.information.name;
        },
        // get web user roles
        async getRoles() {
            try {
                this.roles = await getRoles('web');
                this.roles = this.roles.map(role => role.slug);
                if (!this.user.permissions.superadmin || this.edit) {
                    // if current user is not superadmin or target user is being edited, don't allow setting role to superadmin
                    this.roles = this.roles.filter(role => role !== 'superadmin');
                }
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        }
    },
    watch: {
        // watch changes to role variable
        role(newVal, oldVal) {
            if (newVal === 'superadmin') {
                // if role changed to superadmin, store previous pharmacy_id, clear current pharmacy_id and
                // disallow drugsafe user creation
                this.webUserOriginalPharmacyId = this.webUser.pharmacy_id;
                this.webUser.pharmacy_id = null;
                this.$emit('disableDrugSafeUser', true);
            } else if (oldVal === 'superadmin') {
                // if role changed from superadmin, revert previous pharmacy_id and allow drugsafe user creation
                this.webUser.pharmacy_id = this.webUserOriginalPharmacyId;
                this.$emit('disableDrugSafeUser', false);
            }
        }
    }
};
</script>
